import React from 'react';
import { Card, Tag, Space, Typography, Descriptions, Alert, Divider } from 'antd';
import { 
    ExclamationCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTimeZone, getTimezoneByMode } from '../../../../contexts/TimeZoneContext';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Paragraph } = Typography;

/**
 * 异常报告卡片组件
 * 展示单个异常的详细信息
 */
const AnomalyCard = ({ anomaly, index }) => {
    const {
        id,
        anomaly_type,
        route_code,
        route_instance_id,
        description,
        severity,
        status,  // 【新增】异常状态
        missing_legs,
        matched_flights,
        unmatched_flights
    } = anomaly;

    // 【新增】使用时区选择器
    const { timeZone: timeZoneMode, airportsMap } = useTimeZone();

    // 异常类型映射
    const ANOMALY_TYPES = {
        INCOMPLETE_ROUTE: { 
            label: '不完整航线', 
            icon: <WarningOutlined />,
            color: 'orange'
        },
        UNMATCHED_FLIGHT: { 
            label: '未匹配航班', 
            icon: <QuestionCircleOutlined />,
            color: 'red'
        },
        AMBIGUOUS_MATCH: { 
            label: '模糊匹配', 
            icon: <ExclamationCircleOutlined />,
            color: 'blue'
        }
    };

    // 严重程度映射
    const SEVERITY_MAP = {
        LOW: { label: '低', color: 'default', icon: <InfoCircleOutlined /> },
        MEDIUM: { label: '中', color: 'warning', icon: <WarningOutlined /> },
        HIGH: { label: '高', color: 'error', icon: <ExclamationCircleOutlined /> }
    };

    // 【新增】异常状态映射
    const STATUS_MAP = {
        PENDING_NEXT_BATCH: { 
            label: '待下期确认', 
            color: 'processing',  // 蓝色动态
            icon: <ClockCircleOutlined />,
            description: '批次末尾未匹配，等待下个批次核算'
        },
        CONFIRMED_UNMATCHED: { 
            label: '确认未匹配', 
            color: 'error',  // 红色
            icon: <CloseCircleOutlined />,
            description: '确认为真正的未匹配异常'
        },
        RESOLVED: { 
            label: '已解决', 
            color: 'success',  // 绿色
            icon: <CheckCircleOutlined />,
            description: '跨批次匹配成功，异常已自动解决'
        }
    };

    const typeInfo = ANOMALY_TYPES[anomaly_type] || { 
        label: anomaly_type, 
        icon: <InfoCircleOutlined />,
        color: 'default'
    };
    
    const severityInfo = SEVERITY_MAP[severity] || { 
        label: severity, 
        color: 'default',
        icon: <InfoCircleOutlined />
    };

    // 【新增】获取状态信息
    const statusInfo = STATUS_MAP[status] || { 
        label: status || 'CONFIRMED_UNMATCHED',  // 默认状态
        color: 'default',
        icon: <InfoCircleOutlined />,
        description: ''
    };

    // 合并描述和状态说明
    const alertMessage = statusInfo.description && status !== 'CONFIRMED_UNMATCHED' 
        ? `${description} [${statusInfo.description}]`
        : description;
    
    const alertType = status === 'RESOLVED' 
        ? 'success' 
        : status === 'PENDING_NEXT_BATCH' 
        ? 'info' 
        : (severity === 'HIGH' ? 'error' : severity === 'MEDIUM' ? 'warning' : 'info');

    return (
        <Card 
            style={{ marginBottom: 12 }}
            size="small"
            title={
                <Space>
                    <Text strong>#{index + 1}</Text>
                    <Tag icon={typeInfo.icon} color={typeInfo.color}>
                        {typeInfo.label}
                    </Tag>
                    <Tag icon={statusInfo.icon} color={statusInfo.color}>
                        {statusInfo.label}
                    </Tag>
                    {route_code && <Text type="secondary">({route_code})</Text>}
                </Space>
            }
        >
            {/* 合并的描述信息 */}
            <Alert 
                message={alertMessage}
                type={alertType}
                showIcon
                style={{ marginBottom: 12 }}
            />

            {/* 简化的详细信息 - 只显示核心数据 */}
            {(missing_legs?.length > 0 || matched_flights?.length > 0 || unmatched_flights?.length > 0) && (
                <div style={{ marginTop: 8 }}>
                    {/* 缺失航段 */}
                    {missing_legs && missing_legs.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>缺失航段: </Text>
                            <Space wrap size="small">
                                {missing_legs.map((leg, idx) => (
                                    <Tag key={idx} color="red" style={{ margin: '2px' }}>
                                        {leg.dep} → {leg.arr}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    )}

                    {/* 已匹配航班 - 保留日期时间（使用时区选择器） */}
                    {matched_flights && matched_flights.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>已匹配: </Text>
                            <Space wrap size="small">
                                {matched_flights.map((flight) => {
                                    const timeValue = flight.atd || flight.std;
                                    let timeDisplay = '';
                                    if (timeValue) {
                                        const targetTimezone = getTimezoneByMode(timeZoneMode, flight.departure_airport, airportsMap);
                                        const localTime = dayjs.utc(timeValue).tz(targetTimezone);
                                        timeDisplay = localTime.format('MM-DD HH:mm');
                                    }
                                    return (
                                        <Tag key={flight.flight_id} color="green" style={{ margin: '2px' }}>
                                            {flight.aircraft_reg && `[${flight.aircraft_reg}] `}
                                            {flight.flight_number} {flight.leg}
                                            {timeDisplay && (
                                                <Text style={{ fontSize: 11, marginLeft: 4 }}>
                                                    {timeDisplay}
                                                </Text>
                                            )}
                                        </Tag>
                                    );
                                })}
                            </Space>
                        </div>
                    )}

                    {/* 未匹配航班 - 保留日期时间（使用时区选择器） */}
                    {unmatched_flights && unmatched_flights.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>未匹配: </Text>
                            <Space wrap size="small">
                                {unmatched_flights.map((flight) => {
                                    const timeValue = flight.atd || flight.std;
                                    let timeDisplay = '';
                                    if (timeValue) {
                                        const targetTimezone = getTimezoneByMode(timeZoneMode, flight.departure_airport, airportsMap);
                                        const localTime = dayjs.utc(timeValue).tz(targetTimezone);
                                        timeDisplay = localTime.format('MM-DD HH:mm');
                                    }
                                    return (
                                        <Tag key={flight.flight_id} color="orange" style={{ margin: '2px' }}>
                                            {flight.aircraft_reg && `[${flight.aircraft_reg}] `}
                                            {flight.flight_number} {flight.leg}
                                            {timeDisplay && (
                                                <Text style={{ fontSize: 11, marginLeft: 4 }}>
                                                    {timeDisplay}
                                                </Text>
                                            )}
                                        </Tag>
                                    );
                                })}
                            </Space>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default AnomalyCard;

